<template>
  <div>
    <Breadcrumbs
      :paths="[$route.path]"
    ></Breadcrumbs>
    <v-toolbar>
      <v-toolbar-title>
        {{ $t("message.translation_preset.title") }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" class="ma-2"  :disabled="!refreshable" @click="get_data(get_prefix() + module, get_id())">
              mdi-refresh
            </v-icon>
          </template>
          <span>{{ $t("message.common.refresh") }}</span>
        </v-tooltip>
        <template v-if="mode === 'user'">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="ma-2" @click="$router.push('/' + get_prefix() + 'translation_preset/regist')">
                mdi-plus-box
              </v-icon>
            </template>
            <span>{{ $t("message.common.regist") }}</span>
          </v-tooltip>
        </template>
      </v-toolbar-items>
    </v-toolbar>
    <v-card class="d-flex justify-center mt-1 pl-4">
      <GeneralSelect
        ref="general_select"
        :mode="mode"
        :label="$t('message.common.target')"
        layer_type="layer"
        :target_layer="['user']"
        :target="target"
        v-on:update_target="target = $event; get_data(get_prefix() + module, get_id())"
        v-on:clear_target="reset_target(); data = []"
      >
      </GeneralSelect>
    </v-card>
    <v-card class="d-flex justify-center mt-1">
      <template v-if="mode === 'user'">
        <v-card flat class="pa-2">
          <v-switch
            :value="rerankable && !($vuetify.breakpoint.xs)"
            @change="rerankable = $event"
            :label="$t('message.translation_preset.rerank')"
            :disabled="$vuetify.breakpoint.xs"
          ></v-switch>
        </v-card>
      </template>
      <v-card flat class="pa-2">
        <v-text-field
          prepend-inner-icon="mdi-magnify"
          clearable
          :label="$t('message.common.tid')"
          v-model="search_tid"
          :disabled="rerankable && !($vuetify.breakpoint.xs)"
        >
        </v-text-field>
      </v-card>
      <v-card flat class="pa-2">
        <v-text-field
          prepend-inner-icon="mdi-magnify"
          clearable
          :label="$t('message.common.title')"
          v-model="search_translation_preset"
          :disabled="rerankable && !($vuetify.breakpoint.xs)"
        >
        </v-text-field>
      </v-card>
      <v-card flat class="pa-2">
        <v-select
          :items="lang_pairs_select"
          :label="$t('message.common.direction')"
          item-text="label"
          item-value="value"
          @change="select_lang_pair"
          :disabled="rerankable && !($vuetify.breakpoint.xs)"
        ></v-select>
      </v-card>
    </v-card>
    <v-data-table
      :headers="headers"
      :items="data"
      item-key="tid"
      class="translation-preset-table elevation-1 mt-1"
      :disable-sort="rerankable && !($vuetify.breakpoint.xs)"
      :custom-sort="directionSort"
      :search="search_source_lang || search_target_lang"
      :custom-filter="filter_lang"
      @click:row="($vuetify.breakpoint.xs || rerankable) ? '' : $router.push('/' + get_prefix() + 'translation_preset/' + $event.tid + get_referer(module));"
      :footer-props="{
        'items-per-page-options': [10, 100, 200, 500]
      }"
      v-sortable-data-table="rerankable && !($vuetify.breakpoint.xs)"
      @sorted="update_rank"
      :options.sync="options"
    >
      <template v-slot:item.rank="{ item }">
        {{ item.rank }}
      </template>
      <template v-slot:item.tid="{ item }">
        {{ item.tid }}
      </template>
      <template v-slot:item.name="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              {{ item.name }}
            </span>
          </template>
          <span>{{ item.name }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.source_lang="{ item }">
        {{ $t("message.common.lang_" + item.source_lang) }}
        &rarr;
        {{ $t("message.common.lang_" + item.target_lang) }}
      </template>
      <template v-slot:item.ctime="{ item }">
        {{ $d(1000 * item.ctime, 'long', $store.getters.langs[$i18n.locale].locale) }}
      </template>
      <template v-slot:item.operation="{ item }">
        <template v-if="deletable(item)">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="ma-2" @click.stop="confirm_delete_data(get_prefix() + 'translation_preset', [item.tid], get_id())">
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t("message.common.delete") }}</span>
          </v-tooltip>
        </template>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" class="ma-2" @click.stop="$refs.translation_preset_viewer && $refs.translation_preset_viewer.open(item.title, 'translation_preset', item.tid)">
              mdi-file-find
            </v-icon>
          </template>
          <span>{{ $t("message.viewer.preview") }}</span>
        </v-tooltip>
      </template>
      <template v-if="$vuetify.breakpoint.xs" v-slot:item.action="{ item }">
        <v-btn color="primary" class="mb-2" @click="$router.push('/' + get_prefix() + 'translation_preset/' + item.tid + get_referer(module))">
          {{ $t("message.common.show_details") }}
        </v-btn>
      </template>
    </v-data-table>
    <p class="text-right pt-3">
      <template v-if="mode === 'user'">
        <v-btn
          color="primary"
          :disabled="array_equal(original_rank, rank)"
          @click="change_rank()"
        >
          {{ $t("message.translation_preset.apply_rank") }}
        </v-btn>
      </template>
      <v-btn to="/">
        {{ $t("message.common.return_home") }}
      </v-btn>
    </p>
    <Confirm
      ref="delete_confirm"
      :message="$t('message.common.delete_confirm')"
      :positive="$t('message.common.yes')"
      :negative="$t('message.common.no')"
    >
    </Confirm>
    <TranslationPresetViewer
      ref="translation_preset_viewer"
      :mode="mode"
      :nav_path="nav_path"
    />
    <v-overlay
      :absolute="true"
      :value="progress_overlay"
    >
      <v-progress-circular
        :value="progress"
      >
        {{ progress }}
      </v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Breadcrumbs from '../components/Breadcrumbs';
import GeneralSelect from '../components/GeneralSelect';
import Confirm from '../components/Confirm';
import TranslationPresetViewer from '../components/TranslationPresetViewer';
import { res_list } from "../js/res_list";
import { http_error } from "../js/http_error";
import Sortable from 'sortablejs';

export default {
  name: 'translation_preset',
  components: {
    Breadcrumbs,
    GeneralSelect,
    Confirm,
    TranslationPresetViewer
  },
  mixins: [
    res_list,
    http_error
  ],
  props: {
    mode: String
  },
  data: function(){
    return {
      module_id: "27",
      nav_path: null,
      data: [],
      original_rank: [],
      target: {
        layer: "",
        gid: "",
        uid: ""
      },
      progress: 0,
      progress_overlay: false,
      rerankable: false,
      search_tid: "",
      search_translation_preset: "",
      module: "translation_preset",
      search_source_lang: '',
      search_target_lang: '',
      options: {
        sortBy: ['rank'],
        sortDesc: [false],
      }
    }
  },
  methods: {
    get_data: function(module, extra_path) {
      this.progress_overlay = true;
      this.progress = 0;
      this.data = [];
      this.$http.get(this.$store.getters.api_endpoint + "/" + module + extra_path + "/rank",
                     {
                       onDownloadProgress: function( progressEvent ) {
                         this.progress = parseInt( Math.round( ( progressEvent.loaded / progressEvent.total ) * 100 / 2 ));
                       }.bind(this)
                     })
        .then( response => {
          this.progress = 50;
          if(this.$store.getters.debug){
            console.log("response.data:" + JSON.stringify(response.data));
          }
          this.original_rank = response.data.rank;
          return this.$http.get(this.$store.getters.api_endpoint + "/" + module + extra_path,
                                {
                                  onDownloadProgress: function( progressEvent ) {
                                    this.progress = parseInt( Math.round( ( progressEvent.loaded / progressEvent.total ) * 100 / 2 ) + 50);
                                  }.bind(this)
                                });
        })
        .then( response => {
          this.progress = 100;
          this.progress_overlay = false;
          if(this.$store.getters.debug){
            console.log("response.data:" + JSON.stringify(response.data));
          }
          var data = response.data;
          var data_cache = {};
          for(var d of data){
            data_cache[d.tid] = d;
          }
          var index = 1;
          var rank = [];
          for(var r of this.original_rank){
            if(r.tid in data_cache){
              data_cache[r.tid].rank = index++;
              rank.push(r.tid);
            }
          }
          data.sort((a, b) => a.rank - b.rank);
          this.data = data;
          this.original_rank = rank;
        })
        .catch( error => {
          this.progress_overlay = false;
          if(this.http_error(error)){
            // 404だと何もしない
            if(error.response.status !== 404){
              this.$store.commit("setNotification", {
                type: "error",
                message: error.response.data
              });
            }
          }
        });
    },
    directionSort: function(items, index, isDesc) {
      items.sort(function(a, b){
        if(index[0] === "source_lang"){
          var directionName = function(item){
            return this.$i18n.t("message.common.lang_" + item["source_lang"])
              + this.$i18n.t("message.common.lang_" + item["target_lang"]);
          }.bind(this);
          if (!isDesc[0]) {
            if(directionName(a) < directionName(b)){
              return -1;
            }else if(directionName(a) > directionName(b)){
              return 1;
            }
            return 0;
          }else{
            if(directionName(a) > directionName(b)){
              return -1;
            }else if(directionName(a) < directionName(b)){
              return 1;
            }
            return 0;
          }
        }else{
          if (!isDesc[0]) {
            if(a[index[0]] < b[index[0]]){
              return -1;
            }else if(a[index[0]] > b[index[0]]){
              return 1;
            }
            return 0;
          }else{
            if(a[index[0]] > b[index[0]]){
              return -1;
            }else if(a[index[0]] < b[index[0]]){
              return 1;
            }
            return 0;
          }
        }
      }.bind(this));
      return items;
    },
    get_lang_pair() {
      let lang_pairs_select = [{
        'label': this.$i18n.t("message.translation_preset.langs_all"),
        'value':''
      }];
      // source_lang、target_langを複合した文字列を作成
      let langs_pairs = this.data.map(elem =>{
        elem.langs_pairs = `${elem.source_lang},${elem.target_lang}`;
        return `${elem.source_lang},${elem.target_lang}`
      });
      // 重複を削除
      langs_pairs = [...new Set(langs_pairs)];
      langs_pairs.sort((a, b) =>{
        const a_langs = a.split(',');
        const b_langs = b.split(',');
        if (this.$i18n.t('message.common.lang_' + a_langs[0]) + this.$i18n.t('message.common.lang_' + a_langs[1])
          > this.$i18n.t('message.common.lang_' + b_langs[0]) + this.$i18n.t('message.common.lang_' + b_langs[0])) {
          return 1;
        }else{
          return -1;
        }
      });
      langs_pairs.forEach(elem =>{
        const langs = elem.split(',');
        lang_pairs_select.push({
          'label': `${this.$i18n.t("message.common.lang_" + langs[0])} → ${this.$i18n.t("message.common.lang_" + langs[1])}`,
          'value': elem
        })
      })
      return lang_pairs_select;
    },
    select_lang_pair(select_value){
      const langs = select_value.split(',');
      this.search_source_lang = langs[0];
      this.search_target_lang = langs[1];
    },
    filter_lang(value, search, item) {
      if(this.rerankable && !(this.$vuetify.breakpoint.xs)){
        return true;
      }
      return item.source_lang.indexOf(this.search_source_lang) !== -1 && item.target_lang.indexOf(this.search_target_lang) !== -1;
    },
    update_rank: function(event){
      const movedItem = this.data.splice(this.options.itemsPerPage * (this.options.page - 1) + event.oldIndex, 1)[0];
      this.data.splice(this.options.itemsPerPage * (this.options.page - 1) + event.newIndex, 0, movedItem);
      var index = 1;
      this.data.map((d) => d.rank = index++);
    },
    array_equal: function(a, b){
      if (!Array.isArray(a))    return false;
      if (!Array.isArray(b))    return false;
      if (a.length != b.length) return false;
      for (var i = 0, n = a.length; i < n; ++i) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    },
    change_rank: function(){
      var url = this.$store.getters.api_endpoint + "/translation_preset/rank";
      this.$http.put(url,
                     {
                       rank: this.rank
                     },
                     {
                       headers: {
                         'Content-Type': 'application/json'
                       }
                     })
        .then((response) => {
          this.$store.commit("setNotification", {
            type: "success",
            message: {
              eid: "E1" + this.module_id + "003" + ":00000000",
              param: {}
            }
          });
          this.original_rank = [].concat(this.rank);
        })
        .catch( error => {
          if(this.http_error(error)){
            this.$store.commit("setNotification", {
              type: "error",
              message: error.response.data
            });
          }
        });
    },
  },
  computed: {
    headers: function() {
      let table_headers = [
        {
          text: this.$i18n.t("message.translation_preset.rank"),
          value: 'rank'
        },
        {
          text: this.$i18n.t("message.common.tid"),
          value: 'tid',
          filter: value => {
            if((this.rerankable && !(this.$vuetify.breakpoint.xs)) || this.search_tid === "" || this.search_tid === null){
              return true;
            }
            return value.indexOf(this.search_tid) !== -1;
          }
        },
        {
          text: this.$i18n.t("message.common.title"),
          value: 'title',
          filter: value => {
            if((this.rerankable && !(this.$vuetify.breakpoint.xs)) || this.search_translation_preset === "" || this.search_translation_preset === null){
              return true;
            }
            return value.indexOf(this.search_translation_preset) !== -1;
          },
          cellClass: 'title-column'
        },
        { text: this.$i18n.t("message.common.direction"), value: 'source_lang' },
        { text: this.$i18n.t("message.common.ctime"), value: 'ctime' },
        {
          text: this.$i18n.t("message.common.operation"),
          value: 'operation',
          sortable: false,
        }
      ];
      if(this.$vuetify.breakpoint.xs){
        table_headers.push({
        text: '',
        value: 'action',
        sortable: false
        });
      }
      return table_headers;
    },
    lang_pairs_select: function(){
      return this.get_lang_pair();
    },
    rank: function(){
      return this.data.map(r => r.tid);
    }
  },
  directives: {
    sortableDataTable: {
      bind (el, binding, vnode) {
        var tbody = el.getElementsByTagName('tbody')[0];
        var sortable = null;
        if(Object.keys(tbody).filter(key => key.startsWith("Sortable")).length > 0){
          sortable = tbody[Object.keys(tbody).filter(key => key.startsWith("Sortable"))[0]];
        }
        if (((! binding.hasOwnProperty('value')) || binding.value) && sortable === null) {
          const options = {
            animation: 150,
            onUpdate: function (event) {
              vnode.child.$emit('sorted', event)
            }
          }
          Sortable.create(tbody, options)
        }
      },
      update (el, binding, vnode) {
        var tbody = el.getElementsByTagName('tbody')[0];
        var sortable = null;
        if(Object.keys(tbody).filter(key => key.startsWith("Sortable")).length > 0){
          sortable = tbody[Object.keys(tbody).filter(key => key.startsWith("Sortable"))[0]];
        }
        if (((! binding.hasOwnProperty('value')) || binding.value) && sortable === null) {
          const options = {
            animation: 150,
            onUpdate: function (event) {
              vnode.child.$emit('sorted', event)
            }
          }
          Sortable.create(el.getElementsByTagName('tbody')[0], options)
        }else if((binding.hasOwnProperty('value')) && !binding.value && sortable !== null){
          sortable.destroy();
        }
      }
    }
  }
}
</script>
<style scoped>
.translation-preset-table >>> .title-column {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(50vw - 300px);
}
.v-tooltip__content {
  word-break: break-all;
}
</style>
